import { useReactiveVar } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    PRODUCT_TYPE_ACTIVATION_ADMIN_AUTHOR_MESSAGE,
    PRODUCT_TYPE_ACTIVATION_OM_MESSAGE,
    PRODUCT_TYPE_ACTIVATION_REVIEWER_MESSAGE,
    PT_ACTIVATION_DIALOG_MAX_WIDTH,
    PT_ACTIVATION_DIALOG_WIDTH,
    PT_CHILD_PARA_DEACTIVATE_HEADER_MESSAGE,
} from 'pages/components/constants'
import { onEscKeyPressOnDialog } from 'pages/ProjectsPage/WorkspacePage/components/Common/utils'
import React from 'react'
import { useOMWorkspace } from 'shared/hooks/useOMWorkspace'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            padding: theme.spacing(2, 0),
        },
        maxWidth: pxToRem(PT_ACTIVATION_DIALOG_MAX_WIDTH),
    },

    dialogWrapper: {
        '& .MuiDialog-paperScrollPaper': {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            width: pxToRem(PT_ACTIVATION_DIALOG_WIDTH),
        },
    },
    closeButton: {
        color: '#1899D4',
        fontSize: pxToRem(16),
        fontWeight: 500,
        marginBottom: pxToRem(16),
        border: '1px solid #1899D4',
        borderRadius: pxToRem(50),
        background: 'transparent',
        boxShadow: 'none',
        '&:hover': {
            background: 'transparent',
            boxShadow: 'none',
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            padding: theme.spacing(3.75, 0, 2.5),
        },
    },
    dialogContent: {
        padding: theme.spacing(2.5, 0),
        overflow: 'hidden',
        color: theme.palette.common.black,
        fontSize: pxToRem(14),
        borderTop: 'none',
        borderBottom: '1px solid #ACB1AD',
        maxWidth: pxToRem(PT_ACTIVATION_DIALOG_MAX_WIDTH),
    },
}))

export interface ProductTypeDeactivationWarningProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose: VoidFunction

    /**
     * Overrides the default dialog title.
     */
    titleOverride?: string

    /**
     * Overrides the default role-based message.
     */
    messageOverride?: string

    /**
     * Called after the dialog close animation completes.
     */
    onExited?: VoidFunction
}

const ProductTypeDeactivationWarning = ({
    openDialog,
    handleDialogClose,
    titleOverride,
    messageOverride,
    onExited,
}: ProductTypeDeactivationWarningProps) => {
    const classes = useStyles()
    const currentUserInfo = useReactiveVar(userInfo)
    const { previewFeatures } = usePreviewFeatures()
    const { isCurrentlyOnOMWorkspace } = useOMWorkspace()

    if (!currentUserInfo || currentUserInfo.isReviewer === undefined) {
        return null
    }

    const { isReviewer } = currentUserInfo
    const isOMContext =
        previewFeatures.hasOMPTAutomation && isCurrentlyOnOMWorkspace

    const dialogMessage = isOMContext
        ? PRODUCT_TYPE_ACTIVATION_OM_MESSAGE
        : isReviewer
        ? PRODUCT_TYPE_ACTIVATION_REVIEWER_MESSAGE
        : PRODUCT_TYPE_ACTIVATION_ADMIN_AUTHOR_MESSAGE

    return (
        <Dialog
            open={openDialog}
            data-testid="productTypeRemovalWarning"
            onClose={onEscKeyPressOnDialog(handleDialogClose)}
            className={classes.dialogWrapper}
            TransitionProps={{ onExited }}
        >
            <DialogTitle className={classes.dialogTitle}>
                {titleOverride ?? PT_CHILD_PARA_DEACTIVATE_HEADER_MESSAGE}
            </DialogTitle>
            <DialogContent
                className={classes.dialogContent}
                data-testid="dialogContent"
            >
                {messageOverride ?? dialogMessage}
            </DialogContent>

            <DialogActions className={classes.dialogAction}>
                <Button
                    variant="contained"
                    onClick={handleDialogClose}
                    className={classes.closeButton}
                    data-testid="closeButtonProductTypeWarning"
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ProductTypeDeactivationWarning
